import { FC, HTMLAttributes, useContext, useState } from 'react'
import classNames from 'classnames'
import './TimelineSelectionBody.scss'
import { CutterContext } from '../contexts/CutterContext'
import { PositionRange } from '../../types'
import { useVisiblePixelSize } from '../hooks/useVisiblePixelSize'
import { areRangesDifferent } from '../../functions/areRangesEqual'
import { InterfaceEvent } from '../../interfaces/InterfaceEvent'
import { useDrag } from '../../hooks/useDrag'
import { InternalContext } from '../contexts/InternalContext'
import { useDisableUserSelect } from '../../hooks/useDisableUserSelect'
import { useDocumentCursor } from '../../hooks/useDocumentCursor'
import { logger } from '../../logger/logger'
import { LogActionType } from 'api/logs/log-action-type'

interface Props extends HTMLAttributes<HTMLDivElement> {
  range: PositionRange
}

export const TimelineSelectionBody: FC<Props> = ({ range, className, children, ...attrs }) => {
  const [element, setElement] = useState<HTMLDivElement | null>(null)
  const {
    selection,
    setSelection,
    audio: { length },
  } = useContext(CutterContext)
  const { setInternalSelection, setMouseCursorEnabled } = useContext(InternalContext)
  const [initialState, setInitialState] = useState<{ range: PositionRange; clientX: number } | null>(null)
  const pixelSize = useVisiblePixelSize()

  const onMove = ({ clientX }: InterfaceEvent) => {
    if (initialState === null) {
      return
    }

    const rangeLength = initialState.range[1] - initialState.range[0]
    let delta = Math.round((clientX - initialState.clientX) * pixelSize)
    let nextStart = initialState.range[0] + delta
    let nextEnd = initialState.range[1] + delta

    if (nextStart < 0) {
      setInternalSelection([0, rangeLength])
    } else if (nextEnd > length) {
      setInternalSelection([length - rangeLength, length])
    } else {
      setInternalSelection([nextStart, nextEnd])
    }
  }

  const drag = useDrag(element, {
    onDown({ clientX }: InterfaceEvent) {
      setInitialState({ range, clientX })
      setMouseCursorEnabled(false)
    },
    onMove,
    onUp() {
      if (areRangesDifferent(selection, range)) {
        logger.log(LogActionType.TimelineSelectionChange, {
          selection,
        })

        setSelection(range)
      }

      setMouseCursorEnabled(true)
    },
  })

  useDisableUserSelect(drag)
  useDocumentCursor('grabbing', drag)

  return (
    <div
      className={classNames('TimelineSelectionBody', className, {
        '-grabbing': drag,
      })}
      {...attrs}
      ref={setElement}
    >
      {children}
    </div>
  )
}
